import { combineDataProviders } from 'react-admin';
import jsonServerProvider from 'ra-data-json-server';
import httpClient from './httpClient';
import { getApiBaseUrl } from './apiBaseUrl';
import { getAdminApiBaseUrl } from './adminApiBaseUrl';
import { fetchPendingInquiryIds } from '../inquiries/inquiryWorkflowApi';

const MARKETING_RESOURCES = new Set(['messageinbox', 'leadcompanies', 'leadenrichmentjobs']);

/** React-admin filter forms may store booleans as true, "true", or 1. */
const isReplyAwaitedFilterOn = (value: unknown): boolean =>
  value === true || value === 'true' || value === 1 || value === '1';

const marketingProvider = jsonServerProvider(getApiBaseUrl(), httpClient);
const adminProvider = jsonServerProvider(getAdminApiBaseUrl(), httpClient);

const stripReplyAwaitedFilters = (filter: Record<string, unknown> | undefined) => {
  const {
    reply_awaited: _legacy,
    reply_awaited_only: _toggle,
    ...restFilter
  } = filter || {};
  return restFilter;
};

const adminProviderWithInquiryReplyFilter = {
  ...adminProvider,
  getList: (resource: string, params: Parameters<typeof adminProvider.getList>[1]) => {
    if (resource !== 'inquiries') {
      return adminProvider.getList(resource, params);
    }

    const filter = params.filter || {};
    const restFilter = stripReplyAwaitedFilters(filter);
    // Only the toggle controls filtering (ignore legacy reply_awaited from old dropdown / localStorage).
    const wantsPendingOnly = isReplyAwaitedFilterOn(filter.reply_awaited_only);

    if (!wantsPendingOnly) {
      return adminProvider.getList(resource, { ...params, filter: restFilter });
    }

    const status = restFilter.status != null ? String(restFilter.status) : undefined;

    return fetchPendingInquiryIds(status)
      .then((pendingIds) => {
        const nextFilter = { ...restFilter } as Record<string, unknown>;
        if (!pendingIds.length) {
          return { data: [], total: 0 };
        }
        nextFilter.inquiry_ids = pendingIds.join(',');
        return adminProvider.getList(resource, { ...params, filter: nextFilter });
      })
      .catch((error: { response?: { status?: number }; message?: string }) => {
        const statusCode = error?.response?.status;
        if (statusCode === 404) {
          throw new Error(
            'Reply awaited filter is unavailable (Marketing API missing /inquiry-workflow/pending-inquiry-ids). Redeploy the Marketing API and try again.'
          );
        }
        throw error;
      });
  },
};

const compositeDataProvider = combineDataProviders((resource) => {
  if (MARKETING_RESOURCES.has(resource)) {
    return marketingProvider;
  }
  return adminProviderWithInquiryReplyFilter;
});

export default compositeDataProvider;
